import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../hooks/hooks";
import { dataSearchStatus } from "../../../store/dataSearchSlice";
import { useLoader } from "../../../hooks/common/useLoader";
import { ListTable } from "../../../layout/content/list/ListTable";
import excelIcon from "../../../assets/images/button_icons/excel_icon.svg";
import { useDataSearchFilters } from "../hooks/useDataSearchFilters";
import { useTestResultItemColumns } from "../hooks/useTestResultItemColumns";
import { useTestResultItemList } from "../hooks/useTestResultItemList";
import { useTestResultItemDownloader } from "../hooks/useTestResultItemDownloader";
import { TestResultSearchCondition } from "../../../types/dataSearch";
import { ColumnSelector } from "./ColumnSelector";

export function TestResultItemSearch() {
  const { t } = useTranslation("dataSearch");
  const status = useAppSelector(dataSearchStatus);

  useLoader({ status });

  const { searchTestResultItemList, totalCnt, testResultItemList, pageCondition, getSearchParam } =
    useTestResultItemList({});
  const { filters } = useDataSearchFilters({ search: searchTestResultItemList({}) });
  const { columns } = useTestResultItemColumns();
  const { downloadTestResultItems } = useTestResultItemDownloader();

  const columnMetas = useMemo(
    () => columns.map((c) => ({ key: String(c.accessor), label: String(c.header) })),
    [columns]
  );

  const [selectedKeys, setSelectedKeys] = useState<string[]>(() => columns.map((c) => String(c.accessor)));

  const visibleColumns = useMemo(
    () => columns.filter((c) => selectedKeys.includes(String(c.accessor))),
    [columns, selectedKeys]
  );

  const onClickDownload = useCallback(() => {
    const condition = {
      ...(getSearchParam() as unknown as TestResultSearchCondition),
      columns: selectedKeys,
    };
    downloadTestResultItems(condition);
  }, [downloadTestResultItems, getSearchParam, selectedKeys]);

  return (
    <>
      <ColumnSelector columns={columnMetas} selectedKeys={selectedKeys} onChange={setSelectedKeys} />
      {testResultItemList ? (
        <ListTable
          pageOnFilter={pageCondition}
          filters={filters}
          totalRowsCnt={{
            totalCount: <span>{t("list.totalItemLabel", { count: totalCnt ?? 0 })}</span>,
            singleUnitKeyInCommonFile: "testHistoryUnit.singleComponent",
            multipleUnitKeyInCommonFile: "testHistoryUnit.multipleComponent",
          }}
          columns={visibleColumns}
          listData={testResultItemList}
          getList={({ page, sort, size }) => searchTestResultItemList({ page, sort, size })}
          buttonsNode={
            <>
              <div></div>
              <div>
                <button onClick={onClickDownload}>
                  <img src={excelIcon} alt={t("list.downloadExcel.iconAlt")} />
                  {t("list.downloadExcel.buttonLabel")}
                </button>
              </div>
            </>
          }
        />
      ) : (
        <></>
      )}
    </>
  );
}
